import * as React from 'react';
import { FileText, Shield, AlertCircle, Clock, CheckCircle, ChevronRight, User, DollarSign, Calendar, Phone } from 'lucide-react';
import { List } from '@components/base/data';
import { Text, Badge, Icon } from '@components/base/display';
import { VStack, HStack, Card } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// Sample claim data
interface ClaimData {
  id: string;
  claimNumber: string;
  policyHolder: string;
  type: string;
  amount: string;
  status: 'pending' | 'approved' | 'rejected' | 'in-review';
  date: string;
}

const sampleClaims: ClaimData[] = [
  { id: '1', claimNumber: 'CLM-2024-0001', policyHolder: 'John Smith', type: 'Auto Collision', amount: '$4,500', status: 'approved', date: '2024-01-15' },
  { id: '2', claimNumber: 'CLM-2024-0002', policyHolder: 'Sarah Johnson', type: 'Property Damage', amount: '$12,000', status: 'in-review', date: '2024-01-18' },
  { id: '3', claimNumber: 'CLM-2024-0003', policyHolder: 'Mike Chen', type: 'Medical', amount: '$2,800', status: 'pending', date: '2024-01-20' },
  { id: '4', claimNumber: 'CLM-2024-0004', policyHolder: 'Emily Davis', type: 'Theft', amount: '$8,500', status: 'rejected', date: '2024-01-22' },
  { id: '5', claimNumber: 'CLM-2024-0005', policyHolder: 'Robert Wilson', type: 'Liability', amount: '$25,000', status: 'in-review', date: '2024-01-25' },
];

const statusIcons: Record<ClaimData['status'], typeof CheckCircle> = {
  approved: CheckCircle,
  'in-review': Clock,
  pending: Clock,
  rejected: AlertCircle,
};

const statusColors: Record<ClaimData['status'], 'success' | 'warning' | 'error' | 'default'> = {
  approved: 'success',
  'in-review': 'warning',
  pending: 'default',
  rejected: 'error',
};

const statusBackgroundClasses: Record<ClaimData['status'], string> = {
  approved: 'bg-green-50 text-green-600',
  rejected: 'bg-red-50 text-red-600',
  'in-review': 'bg-amber-50 text-amber-600',
  pending: 'bg-gray-50 text-gray-600',
};

const statusDisplayLabels: Record<ClaimData['status'], string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  'in-review': 'In Review',
  pending: 'Pending',
};

function ListShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [selectedItem, setSelectedItem] = React.useState<string | null>(null);

  const showDividers = playgroundProps.variant === 'divided';

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="list">
          <Card className="max-w-md">
            <List dividers={showDividers}>
              <List.Item
                leading={<Icon icon={Shield} size="sm" />}
                onClick={playgroundProps.interactive ? () => setSelectedItem('item1') : undefined}
                selected={selectedItem === 'item1'}
              >
                Auto Insurance
              </List.Item>
              <List.Item
                leading={<Icon icon={FileText} size="sm" />}
                onClick={playgroundProps.interactive ? () => setSelectedItem('item2') : undefined}
                selected={selectedItem === 'item2'}
              >
                Home Insurance
              </List.Item>
              <List.Item
                leading={<Icon icon={User} size="sm" />}
                onClick={playgroundProps.interactive ? () => setSelectedItem('item3') : undefined}
                selected={selectedItem === 'item3'}
              >
                Life Insurance
              </List.Item>
            </List>
          </Card>
      </ShowcasePreviewSection>

      {/* Basic List */}
      <ShowcaseDemoSection
        title="Basic List"
        description="Simple list with text items"
       
      >
          <Card className="max-w-md">
            <List>
              <List.Item>Auto Insurance Policy</List.Item>
              <List.Item>Home Insurance Policy</List.Item>
              <List.Item>Life Insurance Policy</List.Item>
              <List.Item>Health Insurance Policy</List.Item>
            </List>
          </Card>
      </ShowcaseDemoSection>

      {/* With Dividers */}
      <ShowcaseDemoSection
        title="With Dividers"
        description="List with divider lines between items"
       
      >
          <Card className="max-w-md">
            <List dividers>
              <List.Item>Policy Documents</List.Item>
              <List.Item>Claim History</List.Item>
              <List.Item>Payment Records</List.Item>
              <List.Item>Coverage Details</List.Item>
            </List>
          </Card>
      </ShowcaseDemoSection>

      {/* Interactive Items */}
      <ShowcaseDemoSection
        title="Interactive Items"
        description="Clickable list items with selection state"
       
      >
          <Card className="max-w-md">
            <List dividers>
              <List.Item
                onClick={() => setSelectedItem('nav1')}
                selected={selectedItem === 'nav1'}
                trailing={<Icon icon={ChevronRight} size="sm" />}
              >
                Dashboard
              </List.Item>
              <List.Item
                onClick={() => setSelectedItem('nav2')}
                selected={selectedItem === 'nav2'}
                trailing={<Icon icon={ChevronRight} size="sm" />}
              >
                Policies
              </List.Item>
              <List.Item
                onClick={() => setSelectedItem('nav3')}
                selected={selectedItem === 'nav3'}
                trailing={<Icon icon={ChevronRight} size="sm" />}
              >
                Claims
              </List.Item>
              <List.Item
                onClick={() => setSelectedItem('nav4')}
                selected={selectedItem === 'nav4'}
                trailing={<Icon icon={ChevronRight} size="sm" />}
              >
                Settings
              </List.Item>
            </List>
          </Card>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="List items with leading icons"
       
      >
          <Card className="max-w-md">
            <List dividers>
              <List.Item leading={<Icon icon={Shield} size="sm" />}>
                Coverage Overview
              </List.Item>
              <List.Item leading={<Icon icon={FileText} size="sm" />}>
                Policy Documents
              </List.Item>
              <List.Item leading={<Icon icon={DollarSign} size="sm" />}>
                Payment History
              </List.Item>
              <List.Item leading={<Icon icon={Phone} size="sm" />}>
                Contact Agent
              </List.Item>
            </List>
          </Card>
      </ShowcaseDemoSection>

      {/* With Secondary Text */}
      <ShowcaseDemoSection
        title="With Secondary Text"
        description="List items with supporting text and badges"
       
      >
          <Card className="max-w-md">
            <List dividers>
              {([
                { name: 'Auto Insurance', policy: 'POL-2024-001 | Expires Dec 2024', badgeVariant: 'success' as const, badgeLabel: 'Active' },
                { name: 'Home Insurance', policy: 'POL-2024-002 | Expires Mar 2025', badgeVariant: 'success' as const, badgeLabel: 'Active' },
                { name: 'Life Insurance', policy: 'POL-2023-015 | Expires Feb 2024', badgeVariant: 'warning' as const, badgeLabel: 'Expiring' },
              ]).map((item) => (
                <List.Item
                  key={item.name}
                  leading={<Icon icon={Shield} size="sm" />}
                  trailing={<Badge variant={item.badgeVariant} size="sm">{item.badgeLabel}</Badge>}
                >
                  <VStack gap={0}>
                    <Text size="sm" weight="medium">{item.name}</Text>
                    <Text size="xs" color="muted">{item.policy}</Text>
                  </VStack>
                </List.Item>
              ))}
            </List>
          </Card>
      </ShowcaseDemoSection>

      {/* Disabled Items */}
      <ShowcaseDemoSection
        title="Disabled Items"
        description="List with disabled items that cannot be interacted with"
       
      >
          <Card className="max-w-md">
            <List dividers>
              <List.Item
                onClick={() => setSelectedItem('d1')}
                leading={<Icon icon={FileText} size="sm" />}
              >
                View Policy
              </List.Item>
              <List.Item
                onClick={() => setSelectedItem('d2')}
                leading={<Icon icon={AlertCircle} size="sm" />}
              >
                File Claim
              </List.Item>
              <List.Item
                disabled
                /* v8 ignore start */
                onClick={() => setSelectedItem('d3')}
                /* v8 ignore stop */
                leading={<Icon icon={DollarSign} size="sm" />}
              >
                Make Payment (Unavailable)
              </List.Item>
              <List.Item
                onClick={() => setSelectedItem('d4')}
                leading={<Icon icon={Phone} size="sm" />}
              >
                Contact Support
              </List.Item>
            </List>
          </Card>
      </ShowcaseDemoSection>

      {/* Real-world Claim List */}
      <ShowcaseDemoSection
        title="Real-world Claim List"
        description="Complete claims list with status, amounts, and actions"
       
      >
          <VStack gap={4}>
            <HStack justify="between" align="center" className="px-1">
              <Text size="lg" weight="semibold">Recent Claims</Text>
              <Text size="sm" color="secondary">{sampleClaims.length} claims</Text>
            </HStack>
            <Card>
              <List dividers>
                {sampleClaims.map((claim) => {
                  const StatusIcon = statusIcons[claim.status];
                  return (
                    <List.Item
                      key={claim.id}
                      onClick={() => setSelectedItem(claim.id)}
                      selected={selectedItem === claim.id}
                      leading={
                        <div className={`p-2 rounded-full ${statusBackgroundClasses[claim.status]}`}>
                          <StatusIcon className="w-4 h-4" />
                        </div>
                      }
                      trailing={
                        <VStack gap={0} align="end">
                          <Text size="sm" weight="semibold">{claim.amount}</Text>
                          <Badge variant={statusColors[claim.status]} size="sm">
                            {statusDisplayLabels[claim.status]}
                          </Badge>
                        </VStack>
                      }
                    >
                      <VStack gap={0}>
                        <HStack gap={2} align="center">
                          <Text size="sm" weight="medium">{claim.claimNumber}</Text>
                          <Text size="xs" color="muted">|</Text>
                          <Text size="xs" color="muted">{claim.type}</Text>
                        </HStack>
                        <HStack gap={2} align="center">
                          <Icon icon={User} size="xs" className="text-[#78716C]" />
                          <Text size="xs" color="muted">{claim.policyHolder}</Text>
                          <Icon icon={Calendar} size="xs" className="text-[#78716C]" />
                          <Text size="xs" color="muted">{claim.date}</Text>
                        </HStack>
                      </VStack>
                    </List.Item>
                  );
                })}
              </List>
            </Card>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const ListShowcase: ShowcaseDefinition = {
  id: 'list',
  name: 'List',
  category: 'data',
  description: 'Vertical list for displaying collections of items',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['default', 'divided'],
      defaultValue: 'default',
      description: 'Visual variant (default or with dividers)',
    },
    {
      name: 'interactive',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether items are clickable',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="list" (implicit on ul)',
      'role="listitem" (implicit on li)',
      'aria-pressed on interactive items',
      'aria-disabled on disabled items',
    ],
    keyboardNav: [
      'Tab: Move focus through interactive items',
      'Enter/Space: Activate focused item',
    ],
    notes: [
      'Interactive items render as buttons for proper semantics',
      'Focus indicators visible on interactive elements',
      'Disabled state prevents interaction and reduces opacity',
      'Selected state indicated visually with left border',
    ],
  },
  component: ListShowcaseComponent,
};
